import { useEditTenantModal } from 'TenantModule/hooks/useEditTenantModal';
import { Tenant } from 'TenantModule/types';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleMenu from 'components/SimpleMenu';
import { useCallback, useState } from 'react';
import { useArchiveTenantConfirmationModal } from 'TenantModule/hooks/useArchiveTenantConfirmationModal';
import { useCreateTenantRequestModal } from 'TenantModule/hooks/useCreateTenantRequestModal';
import { useTenantLifecycleModal } from 'TenantModule/hooks/useTenantLifecycleModal';
import { useTenants } from 'TenantModule/hooks/useTenants';
import * as constants from '@constants';

export const useTenantsTableBulkSelectMenu = () => {
  const { openModal } = useDynamicModal();
  const { tenantsHash } = useTenants({ isOff: false });
  const [selectedTenantIds, setSelectedTenantIdsSet] = useState<Set<number>>(new Set());
  const numSelected = selectedTenantIds.size;
  const selectedTenantIdsArray = Array.from(selectedTenantIds);
  const tenantIdToEdit = selectedTenantIdsArray[0];
  const selectedTenant = tenantsHash[tenantIdToEdit];
  const { openArchiveTenantConfirmationModal } = useArchiveTenantConfirmationModal();
  const { openEditTenantModal } = useEditTenantModal();
  const { openCreateTenantRequestModal } = useCreateTenantRequestModal();
  const { openTenantLifecycleModal } = useTenantLifecycleModal();

  const selectTenant = ({ tenantId }: { tenantId: Tenant['id'] }) => {
    setSelectedTenantIdsSet((prev) => {
      const isChecked = prev.has(tenantId);
      if (isChecked) {
        prev.delete(tenantId);
      } else {
        prev.add(tenantId);
      }

      return new Set(prev);
    });
  };

  const clearAll = () => {
    setSelectedTenantIdsSet(new Set());
  };

  const openTenantTableBulkSelectMenu = useCallback(
    ({ target }: { target: HTMLElement }) => {
      const menuItems = [
        {
          label: 'Archive Tenant',
          onClick: () => {
            openArchiveTenantConfirmationModal({
              tenantIds: selectedTenantIdsArray,
              onArchiveTenantSuccess: () => {
                clearAll();
              },
            });
          },
        },
        {
          label: 'Edit Tenant',
          // !== 1, not > 1: with nothing selected tenantIdToEdit is undefined and
          // the modal would open targetless.
          disableButton: numSelected !== 1,
          tooltip: numSelected > 1 ? 'Cannot edit multiple tenants at the same time' : '',
          onClick: () => {
            if (numSelected !== 1 || tenantIdToEdit === undefined) {
              return;
            }
            openEditTenantModal({
              tenantId: tenantIdToEdit,
              onEditTenantSuccess: () => {
                clearAll();
              },
            });
          },
        },
        {
          label: 'Lifecycle & readiness',
          disableButton: numSelected !== 1,
          tooltip: numSelected > 1 ? 'Lifecycle actions apply to one tenant at a time' : '',
          // The disable already enforces single-select; the guard makes the
          // single-target contract explicit even if the menu is reused elsewhere.
          onClick: () => {
            if (numSelected !== 1 || tenantIdToEdit === undefined) {
              return;
            }
            openTenantLifecycleModal({
              tenantId: tenantIdToEdit,
              // Unarchive/decommission move the row out of the current table view;
              // a lingering hidden selection would keep Modify armed for it.
              onTenantChanged: () => {
                clearAll();
              },
            });
          },
        },
        {
          label: 'View activity log',
          onClick: () => {
            // Open activity log in new tab with tenant pre-populated
            const params = new URLSearchParams({
              tenant: selectedTenant?.tenantName || '',
            });
            window.open(
              `${constants.ACTIVITY_LOG_PATH}?${params.toString()}`,
              '_blank',
              'noopener,noreferrer',
            );
          },
          disableButton: numSelected !== 1,
          tooltip: numSelected > 1 ? 'Cannot view activity log for multiple tenants' : '',
        },
        {
          label: 'Clear All',
          disableButton: !numSelected,
          onClick: () => {
            clearAll();
          },
        },
      ];

      openModal({
        modal: ({ isOpen: _isOpen, toggle }) => {
          return <SimpleMenu anchorEl={target} items={menuItems} handleClose={toggle} />;
        },
      });
    },

    [
      numSelected,
      openArchiveTenantConfirmationModal,
      openEditTenantModal,
      openTenantLifecycleModal,
      openModal,
      selectedTenantIdsArray,
      tenantIdToEdit,
      selectedTenant?.tenantName,
    ],
  );

  return {
    openTenantTableBulkSelectMenu,
    setSelectedTenantIdsSet,
    selectedTenantIds,
    selectTenant,
  };
};
